import { Dialog, DialogHeading, useDialogStore } from '@ariakit/react/dialog';
import cn from 'classnames';
import { format as formatDate, isValid, parseISO } from 'date-fns';
import { BellIcon, ExternalIcon } from '#assets/ReactSvg.ts';
import { CloseButton } from '#components/atoms/buttons/icon/CloseButton.tsx';
import { EntityIcon } from '#components/atoms/icons/EntityIcon.tsx';
import { Link } from '#components/atoms/link/Link.tsx';
import type { Update, UpdateLink } from '#global/updates/updates.ts';
import styles from './UpdatesPanel.module.css';

type Props = {
  isOpen: boolean;
  onClose: () => void;
  updates: readonly Update[];
};

/**
 * {@link UpdatesPanel} is the slideout listing product updates, newest first.
 *
 * Entries carry no per-item "new" marker: the panel's own title frames the list, and the dates
 * already say which end of it is recent.
 */
export function UpdatesPanel({ isOpen, onClose, updates }: Props) {
  const dialog = useDialogStore({ open: isOpen });

  if (!isOpen) return null;

  return (
    <Dialog
      store={dialog}
      onClose={onClose}
      className={styles.UpdatesPanel}
      backdrop={<div className={styles.UpdatesPanel_overlay} data-testid="updates-overlay" />}
    >
      <header className={styles.UpdatesPanel_header}>
        <DialogHeading className="h4 mw-0 m-0">Notifications</DialogHeading>
        <CloseButton size={16} onClick={onClose} />
      </header>
      <div className={styles.UpdatesPanel_body}>
        {updates.length === 0 ?
          <EmptyUpdates />
        : <ol className={styles.UpdatesPanel_list}>
            {updates.map((update) => (
              <UpdateEntry key={update.id} update={update} onNavigate={onClose} />
            ))}
          </ol>
        }
      </div>
    </Dialog>
  );
}

/** Shown when there are no entries, which is also how the panel ships. */
function EmptyUpdates() {
  return (
    <div className={styles.UpdatesPanel_empty}>
      {/* The same badge a host wears, so the panel's one visual is a shape people already know */}
      <EntityIcon className="mb-16" icon={<BellIcon width={24} height={24} aria-hidden={true} />} />
      {/* <h2> because Ariakit's DialogHeading renders the panel title as an <h1> */}
      <h2 className="h4 m-0">No new notifications</h2>
    </div>
  );
}

type EntryProps = {
  update: Update;
  onNavigate: () => void;
};

function UpdateEntry({ update, onNavigate }: EntryProps) {
  const { date, title, summary, bullets, link } = update;

  // Parsed as local midnight so the day can't shift a zone. `format` throws on bad input and this
  // renders outside the page error boundary, so an unparseable date shows raw.
  const parsedDate = parseISO(date);

  return (
    <li className={styles.UpdatesPanel_item}>
      <div className="mb-4">
        <time className="p-sm" dateTime={date}>
          {isValid(parsedDate) ? formatDate(parsedDate, 'MMMM d, yyyy') : date}
        </time>
      </div>
      {/* <h2>, like the empty state: DialogHeading renders the panel title as an <h1> */}
      <h2 className={cn('h6', styles.UpdatesPanel_title)}>{title}</h2>
      <p className="p-md mb-0">{summary}</p>
      {bullets?.length ?
        <ul className={cn('ul-md', styles.UpdatesPanel_bullets)}>
          {bullets.map((bullet, index) => (
            // Static list, never reordered — and bullet text is not guaranteed unique
            // eslint-disable-next-line react/no-array-index-key
            <li key={index}>{bullet}</li>
          ))}
        </ul>
      : null}
      {link ?
        <div className={styles.UpdatesPanel_linkRow}>
          <EntryLink link={link} onNavigate={onNavigate} />
        </div>
      : null}
    </li>
  );
}

/** `.a` carries the accent colour and focus ring; `.p-md` matches the summary's size and weight. */
function EntryLink({ link, onNavigate }: { link: UpdateLink; onNavigate: () => void }) {
  if (link.external) {
    return (
      <a className="a p-md" href={link.to} target="_blank" rel="noreferrer">
        {link.label}
        <span className="visually-hidden"> (opens in a new tab)</span>
        <ExternalIcon width={10} height={10} className={styles.UpdatesPanel_externalIcon} aria-hidden={true} />
      </a>
    );
  }

  // Following an in-app link leaves nothing to look at behind the panel, so close it on the way.
  return (
    <Link className="a p-md" to={link.to} onClick={onNavigate}>
      {link.label}
    </Link>
  );
}
